// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from "vuex";
import alert from "../../../share/alert";
import loading from "../../../share/components/vue/ProcessingIcon";
import fullModal from "../../../share/components/vue/FullModal";
import noTextSVGButton, { ICON_KEY, BUTTON_TYPE } from "../../../share/components/vue/NoTextSVGButton";
import WrappedTeleport from "../../../share/components/vue/WrappedTeleport";
import config from "../../../share/config";
import individualDiagnose from "../../../share/util/individualDiagnose";
import util from "../../../share/util/utils";
import customProgress from "../../../share/components/vue/CustomProgress";
import memoPage from "../../vue/MemoPage";
import dataMonitor from "../../vue/dataMonitor/DataMonitor";
import dmUtil, { TAB } from "../dataMonitor/dataMonitorUtil";
import { createZoomTabHandlersForOptions, isResetZoomDisabled } from '../dataMonitor/dataMonitorZoomUtil';
import { CsvFileWriter, getMeasurementFileName } from '../dataMonitor/dataMonitorFileUtil';
import { exportCsv } from '../dataRecollection/dataMonitor/csv';

export default {
  name: "individualDataMonitor",
  data() {
    return {
      processing: false,
      showCsvProgress: false, // CSVエクスポートの進捗表示フラグ
      currentWizard: "",
      // システム間移動判定
      isUseCache: undefined,
      menuResponse: null,
      params: null,
      previousName: "",
      queryName: "",
      // ECU項目選択画面表示フラグ
      showSelect: true,
      // ECU計測中フラグ
      inExecution: false,
      // 計測データ再生中フラグ
      inPlaying: false,
      destinationAPI: config.DMAT,
      navItemLabel: "individual.individual_diagnosis_function_data_monitor",
      measureId: "", // 計測ID、計測時に取得
      isSpecial: false,
      isObd: false,
      modalMemo: false,
      checkLines: 0,  // グラフ項目件数
      currentTab: TAB.LIST,
      isZoomPanActive: false,
      isMemoDialogLoading: false, // memo画面でAPIと通信しているかのフラグ
      dataMonitorFunctionSettings: new dmUtil.DataMonitorFunctionSettings(dmUtil.TYPE_CALLER.DEFAULT), // DM機能の設定インスタンス
      // トレーニングモード時のメモ情報保持変数
      memoData: {
        diagnoseComment: null,
        dataName: "data1",
        memo: null
      },
      // OBD互換用。コンソールエラーが表示されるため定義のみ追加。
      obdFunctionLabelKey: "",
      // 全画面モーダルの表示有無
      showModal: false,
      // Measureコンポーネントの描画完了したか否か。Measureコンポーネントへのteleportを描画完了まで待つ必要があるため、チェックする。
      measureMounted: false,
      // SVGアイコンの種類
      ICON_KEY: ICON_KEY,
      // SVGアイコンのボタンの種類
      BUTTON_TYPE: BUTTON_TYPE,
      exportPercentage: 0, // CSVエクスポートの進捗率
    };
  },

  props: {
    // noop
  },

  components: {
    "processing-view": loading,
    "data-monitor": dataMonitor,
    "memo-page": memoPage,
    "full-modal": fullModal,
    "no-text-svg-button": noTextSVGButton,
    "wrapped-teleport": WrappedTeleport,
    "custom-progress": customProgress,
  },

  watch: {
    language: {
      immediate: true,
      handler() {
        if (!this.menuResponse) {
          return;
        }
        this.updateFilter();
      },
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      netAppId: (state) => state.netAppConnector.pairingAppId,
      vehicleInfo: (state) => state.vehicle.vehicleInfo,
      individualMenus: state => state.vehicle.individualMenus,
      isTestMode: state => state.netAppConnector.isTestMode,
      isPopstateNavigation: state => state.cache.isPopstateNavigation,
    }),

    btnDisabled() {
      const disabled = {
        back: this.inExecution || this.inPlaying,
        execute: this.inPlaying,
        memo: this.inExecution,
        graph: !this.checkLines || this.isZoomPanActive,
        resetZoom: isResetZoomDisabled({
          inExecution: this.inExecution,
          inPlaying: this.inPlaying,
          hasCheckLines: Boolean(this.checkLines),
          currentTab: this.currentTab,
        }),
        export: this.inExecution || !this.measureId
      };
      return disabled;
    },

    projectId() {
      return this.vehicleInfo?.vehicle?.project.id || '';
    },
  },

  // 遷移してきた画面のパラメータ情報を保持
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      // 遷移してきたときのコンポーネント内ガード共通処理
      vm.beforeRouteProcess(vm, from);
    });
  },

  /**
   * 遷移してきた画面のパラメータ情報を保持
   * 同一コンポーネントが再帰的に利用された場合、呼び出される
   */
  beforeRouteUpdate(to, from, next) {
    if (this.isPopstateNavigation) {
      this.cacheIsPopstateNavigation(false);
      next();
      return;
    }
    this.$nextTick(() => {
      // 遷移してきたときのコンポーネント内ガード共通処理
      this.beforeRouteProcess(this, from);
      // mounted時に行う共通処理を呼び出す
      this.mountedLogic();
    });
    next();
  },

  /** 画面遷移時、計測中か判定 */
  beforeRouteLeave(to, from, next) {
    if ((to.name === "login") && (to.fullPath === "/") && (to.path === "/")) {
      this.$refs.data_monitor.setSignout();
      next();
      return;
    }
    if (to.name === "changeUnit") {
      if (!this.showSelect) {
        // 単位設定は移動不可
        this.changeUnitAlert();
        return;
      }
    }
    // 計測中は移動不可
    if (this.inExecution) {
      this.inExecutionAlert();
    } else {
      this.showSelect = true;
      next();
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),
    ...mapActions('cache', ['cacheIsPopstateNavigation']),
    ...createZoomTabHandlersForOptions({
      isResetZoomDisabled() {
        return this.btnDisabled.resetZoom;
      },
    }),

    /**
     * componentのmounted時に呼び出す処理
     */
    mountedLogic() {
      this.inExecution = false;
      this.showSelect = true;
    },

    /** ウィザードを閉じた後の処理 */
    closeWizard() {
      this.currentWizard = "";
    },

    /** 項目選択/計測機能切替 */
    toggleShowSelect(flg) {
      if (flg === undefined || flg === null) {
        this.showSelect = !this.showSelect;
      } else {
        this.showSelect = flg;
      }
      if (this.showSelect) {
        this.measureMounted = false;
      }
    },

    /** 計測開始/停止状態切替 */
    toggleExecute(flg) {
      if (flg === undefined || flg === null) {
        this.inExecution = !this.inExecution;
      } else {
        this.inExecution = flg;
      }
    },

    /** 計測データ再生/停止状態切替 */
    togglePlaying(flg) {
      if (flg === undefined || flg === null) {
        this.inPlaying = !this.inPlaying;
      } else {
        this.inPlaying = flg;
      }
      if (this.inPlaying) {
        this.isZoomPanActive = false;
      }
    },

    /** モニタ開始 */
    startMonitor() {
      this.$refs.data_monitor.startMonitor();
    },

    /** モニタ停止 */
    stopMonitor() {
      this.$refs.data_monitor.stopMonitor();
    },

    /** 項目選択に戻る */
    backSelect() {
      // 計測中は移動不可
      if (this.inExecution) {
        this.inExecutionAlert();
      }
      else {
        // 初期化
        this.checkLines = 0;
        this.toggleShowSelect(true);
      }
    },

    /** 計測IDを変更 */
    changeMeasureId(id) {
      this.measureId = id;
    },

    /** グラフ項目件数を変更 */
    changeCheckValue(checkLines) {
      this.checkLines = checkLines;
    },

    /**
     * ズーム/パン状態を更新
     * @param {boolean} isActive
     */
    zoomPanStateChanged(isActive) {
      this.isZoomPanActive = isActive;
    },

    /** メモを開く */
    openMemo() {
      this.modalMemo = true;
      // トレーニングモード用のメモ情報設定
      if (this.isTestMode) {
        this.$nextTick(() => {
          this.$refs.memo.diagnoseComment = this.memoData.diagnoseComment;
          this.$refs.memo.dataName = this.memoData.dataName;
          this.$refs.memo.memo = this.memoData.memo;
        });
      }
    },
    // メモ情報の保存
    async memoSave() {
      // トレーニングモード用のメモ情報設定
      if (this.isTestMode) {
        this.processing = true;
        this.memoData.diagnoseComment = this.$refs.memo.diagnoseComment;
        this.memoData.dataName = this.$refs.memo.dataName;
        this.memoData.memo = this.$refs.memo.memo;
        await util.sleep(1000);
        this.processing = false;
      }
      await this.$refs.memo.save();
    },
    // メモ情報のクリア
    memoClear: function () {
      this.$refs.memo.clear();
    },
    // メモを閉じる
    closeNoteModal() {
      this.modalMemo = false;
    },

    // グラフ設定
    handleConfigureGraph() {
      this.$refs.data_monitor.handleConfigureGraph();
    },

    /**
     * データモニタの多言語変換の一覧取得
     * @param {Array} menuResponse メニュー一覧
     */
    getDmi18nMenuList(menuResponse) {
      const findFFDi18nKey = individualDiagnose.getFFDi18nKey(this, this.individualMenus ? this.individualMenus.menu_list : []);
      /**
       * 翻訳キーとメッセージキーを元に文言が存在すれば、その翻訳後の文言を返す。
       * ない場合は、defaultMessage を返す（defaultMessage 未指定の場合は空文字を返す）
       * @param {string} i18nDmMenuKey 翻訳キー
       * @param {string} messageKey メッセージキー
       * @returns {string | undefined} 翻訳後の文言（見つからなかった場合は undefined）
       */
      const convertI18nText = (i18nDmMenuKey, messageKey) => {
        const key = `${i18nDmMenuKey}.${messageKey}`;
        return this.$te(key) ? this.$t(key) : undefined;
      };

      // 多言語変換リストの取得
      return menuResponse
        .map((item) => {
          const i18nDmMenuKey = `${findFFDi18nKey}.${item.data_monitor_id}`;
          return {
            data_monitor_id: item.data_monitor_id,
            sort_id: item.sort_id,
            menu_title: convertI18nText(i18nDmMenuKey, "menu_title"),
            menu_description:
              convertI18nText(i18nDmMenuKey, "menu_description") || "",
          };
        })
        .sort(this.itemComparator);
    },

    /**
     * 呼び出し時のコンポーネント内ガードの共通処理
     * @param vm 呼び出し元のオブジェクト(this)
     * @param from 遷移元
     */
    beforeRouteProcess(vm, from) {
      if (util.isFromSettingsPage(from.name) || util.isSameSystemPage(from)) {
        // 単位設定画面またはパスワード変更画面からの遷移時、または同じシステム内の子機能への遷移の場合 trueを指定
        vm.isUseCache = true;
      } else {
        // 上記以外の場合、falseを指定
        vm.isUseCache = false;
      }
      // 遷移元画面のパラメータを取得
      if (from.params) {
        // 元々は初期化通信エラー発生時に前の画面に戻る処理が入っていたが、今は前の画面に戻る処理が無くなったため、使用しない
        vm.previousName = from.name;
        vm.params = from.params;
        vm.queryName = from.query;
      }
    },

    /** 計測中警告ダイアログ */
    inExecutionAlert() {
      alert.showWarning(
        this,
        null,
        this.$t("individual.alert_data_monitor_measurement"),
        () => { }
      );
    },

    /** 単位設定警告ダイアログ */
    changeUnitAlert() {
      const label = "individual.alert_data_monitor_change_unitsetting";
      alert.showWarning(this, null, this.$t(label), () => { });
    },

    /**
     * 最大化ボタン押下処理
     */
    maximum() {
      this.showModal = true;
    },

    /**
     * 最小化ボタン押下処理
     */
    minimum() {
      this.showModal = false;
    },

    /**
     * 計測画面全体をteleportさせるモーダルダイアログのIDを作成。再生画面でのID被りを避けるため、画面ごとの値を設定
     */
    modalContentId() {
      return `full-modal-content-${this.dataMonitorFunctionSettings.typeCaller}`;
    },

    /**
     * メモボタンやグラフ設定ボタンをteleportさせる遷移先DOMのIDを作成。再生画面でのID被りを避けるため、画面ごとの値を設定
     * @returns {string} 作成したID
     */
    graphSettingsDestinationId() {
      return `graph-settings-destination-${this.dataMonitorFunctionSettings.typeCaller}`;
    },

    /**
     * 子コンポーネントからDataMonitorMeasureのMount・Unmountの通知を受け取る
     * @param {boolean} isMounted Mountされたか否か
     */
    measureMountedEvent(isMounted) {
      this.measureMounted = isMounted;
      if (!isMounted) {
        // 計測画面のコンポーネントが破棄されたら、計測画面を離れたと判断して全画面モーダルも閉じる
        this.showModal = false;
      }
    },

    /**
     * CSVエクスポートの進捗更新
     * @param {number} allCount 全体数
     * @param {number} currentCount 現在の数
     */
    updateExportProgress(allCount, currentCount) {
      this.exportPercentage = dmUtil.calculatePercentage(allCount, currentCount);
    },

    /**
     * ファイルエクスポートボタン押下時の処理
     */
    async csvExport() {
      let csvWriter = null;
      try {
        this.showCsvProgress = true;
        this.exportPercentage = 0;
        const isMultiple = this.dataMonitorFunctionSettings.isMultipleDataMonitor();
        let measurementId = this.$refs.data_monitor.getMeasurementId();
        const functionNameKey = isMultiple ? 'message.label_menu_multi_data_monitor' : 'individual.individual_diagnosis_function_data_monitor';

        // project情報を取得
        const endPoint = '/projects/' + this.vehicleInfo.vehicle.project.id + '/measures';
        const { dataName, projectDetails } = await dmUtil.fetchProjectDetails(this.destinationAPI, endPoint, this.vehicleInfo, measurementId, this.isTestMode, this.memoData);
        if (this.isTestMode) {
          // トレーニングモードの場合は固定のレスポンスのため、レスポンスに含まれる計測IDを使用して、後続の処理に影響がないようにする
          measurementId = projectDetails.measures[0][this.destinationAPI][0].measurement_id;
        }

        const functionName = this.$t(functionNameKey);
        const fileName = getMeasurementFileName(dataName, functionName, this.vehicleInfo.vehicle.vin.vin ?? '');

        csvWriter = new CsvFileWriter(fileName);
        await csvWriter.init();

        const exportCsvParams = {
          csvFileWriter: csvWriter,
          currentComponent: this,
          dataMonitorComponent: this.$refs.data_monitor,
          projectDetails,
          measurementId,
          storage: dmUtil.DATA_STORAGE.INTDASH,
          errorDialogCallback: () => { },
          updateExportProgress: this.updateExportProgress,
          isDebug: false,
        };
        await exportCsv(exportCsvParams);
      } catch (error) {
        if (CsvFileWriter.isUserCancel(error)) {
          // ユーザーがCSVの書き込みをキャンセルしたら何もせず終了
          return;
        }
        alert.showError(this, null, this.$t('message.common_systemError'), () => { });
      } finally {
        this.showCsvProgress = false;
        csvWriter && await csvWriter.close();
      }
    }
  },

  created() {
    // noop
  },

  mounted() {
    this.mountedLogic();
  },
};
